// Client-side game helpers on top of @wbwl/rules. The browser runs the same rules as the API, so
// a bad word is caught instantly; the server still checks every move and its answer wins.
import type { EditionInfo, Play } from '@wbwl/contract'
import {
  applyMove, applyPhoenix, burnCols, isBurn, rankOf, scorePlay,
  type Edition, type MoveRejection, type PhoenixRejection, type PlayState,
} from '@wbwl/rules'

export const APP_NAME = 'Letters on Fire'
export const SITE = 'https://lettersonfire.com'

export function toEdition(info: EditionInfo): Edition {
  return {
    number: info.number,
    date: info.date,
    seed: '',
    rulesVersion: info.rulesVersion,
    wordsVersion: info.wordsVersion,
    gold: info.gold,
    burns: info.burns,
  }
}

export function toState(play: Play): PlayState {
  return { rows: play.rows, ashes: play.ashes, phase: play.phase, rowsEnd: play.rowsEnd, phoenix: play.phoenix, phoenixResult: play.phoenixResult }
}

/** A Play built locally from a state, used for the instant update before the server answers. */
export function localPlay(info: EditionInfo, prev: Play, state: PlayState): Play {
  const scores = scorePlay(info, state)
  const st = rankOf(scores.total, info.ranks)
  return {
    ...prev,
    rows: state.rows,
    ashes: state.ashes,
    phase: state.phase,
    rowsEnd: state.rowsEnd,
    phoenix: state.phoenix,
    phoenixResult: state.phoenixResult,
    scores,
    standing: { index: st.index, name: st.rank.name, next: st.next, toNext: st.toNext },
    startedAt: prev.startedAt ?? new Date().toISOString(),
  }
}

/** The row (1-based) each burned letter burned on. */
export function burnedOnRow(info: EditionInfo, rows: readonly string[]): Record<string, number> {
  const at: Record<string, number> = {}
  rows.forEach((w, r) => burnCols(info, r).forEach((c) => { if (!(w[c] in at)) at[w[c]] = r + 1 }))
  return at
}

/** Letters in the word being typed that would burn on Enter (not already burned). */
export function lettersToBurn(info: Pick<EditionInfo, 'gold' | 'burns'>, row: number, typing: string, ashes: readonly string[]): string[] {
  return burnCols(info, row).map((c) => typing[c]).filter((l): l is string => !!l && !ashes.includes(l))
}

export function rejectionText(r: MoveRejection | PhoenixRejection, word: string): string {
  switch (r.reason) {
    case 'wrong_length': return 'Five letters, please'
    case 'not_a_word': return `${word} isn't in the word list`
    case 'already_played': return `You played ${word} on row ${r.row + 1}`
    case 'burned_letter': return `${r.letters.join(', ')} already burned`
    case 'not_in_ashes': return `${r.letters.join(', ')} ${r.letters.length === 1 ? "isn't" : "aren't"} in your ashes`
    case 'repeated_letter': return `Each ash once — ${r.letters.join(', ')} is already used`
    case 'wrong_phase': return r.phase === 'done' ? 'This edition is finished' : 'Not now'
  }
}

export { applyMove, applyPhoenix, burnCols, isBurn }

/** Gold letters grouped by tier: [[1, ['R','N','T']], [2, …], [3, …]]. */
export function goldByTier(gold: EditionInfo['gold']): [number, string[]][] {
  const tiers = new Map<number, string[]>()
  for (const [l, t] of Object.entries(gold)) tiers.set(t, [...(tiers.get(t) ?? []), l])
  return [...tiers.entries()].sort((a, b) => a[0] - b[0]).map(([t, ls]) => [t, ls.sort()])
}

export const fmt = (n: number) => n.toLocaleString('en-US')

/** "SAT · OCT 18" */
export function shortDate(iso: string): string {
  return new Date(iso + 'T12:00:00Z').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' }).toUpperCase().replace(',', ' ·')
}

/** "Saturday" */
export const weekday = (iso: string) => new Date(iso + 'T12:00:00Z').toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' })

/** "Sat, Oct 17" */
export const dayDate = (iso: string) => new Date(iso + 'T12:00:00Z').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' })

/** "Saturday, October 18, 2026" */
export function longDate(iso: string): string {
  return new Date(iso + 'T12:00:00Z').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
}

/** Countdown text to an ISO instant, "06:12:44". */
export function countdown(to: string, now = Date.now()): string {
  const s = Math.max(0, Math.round((Date.parse(to) - now) / 1000))
  const p = (n: number) => String(n).padStart(2, '0')
  return `${p(Math.floor(s / 3600))}:${p(Math.floor((s % 3600) / 60))}:${p(s % 60)}`
}

export const storage = {
  get(key: string): string | null { try { return localStorage.getItem(key) } catch { return null } },
  set(key: string, value: string) { try { localStorage.setItem(key, value) } catch { /* private mode */ } },
}

const RETURN_KEY = 'lof:after-signin'
const RETURN_MS = 30 * 60_000

/** Comes back to a challenge after a sign-in started from it, if the sign-in finishes within half an hour. */
export const returnAfterSignIn = (path: string) => storage.set(RETURN_KEY, JSON.stringify({ path, at: Date.now() }))

/** Where a sign-in that just finished should go, once; null when it wasn't started from a challenge. */
export function takeReturnAfterSignIn(): string | null {
  const raw = storage.get(RETURN_KEY)
  storage.set(RETURN_KEY, '')
  try {
    const { path, at } = JSON.parse(raw ?? '') as { path: unknown; at: unknown }
    return typeof path === 'string' && path.startsWith('/c/') && typeof at === 'number' && Date.now() - at < RETURN_MS ? path : null
  } catch {
    return null
  }
}
